import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  uploadVnEvisaFile,
  createVnEvisaApplication,
  getVnEvisaStatus,
  listVnEvisaTasks,
  getVnEvisaLocations,
  getVnEvisaBorderGates,
  getVnEvisaCaptchaImage,
  submitVnEvisaCaptcha,
  retryVnEvisaTask,
} from '../../services/vnEvisaService';
import FALLBACK_VIETNAM_PROVINCES from './vnLocations';

const PASSPORT_TYPES = [
  { value: 'Ordinary passport', label: '普通护照 (Ordinary)' },
  { value: 'Diplomatic passport', label: '外交护照 (Diplomatic)' },
  { value: 'Official passport', label: '公务护照 (Official)' },
  { value: 'Other', label: '其他 (Other)' },
];
const GENDER = [
  { value: 'Male', label: '男 Male' },
  { value: 'Female', label: '女 Female' },
];
const OCCUPATIONS = [
  { value: 'Businessman', label: '商人 Businessman' },
  { value: 'Employee', label: '职员 Employee' },
  { value: 'Official', label: '公务员 Official' },
  { value: 'Retired', label: '退休人员 Retired' },
  { value: 'Student', label: '学生 Student' },
  { value: 'Unemployed', label: '自由职业/无业 Unemployed' },
  { value: 'Others', label: '其他 Others' },
];
const PURPOSES = [
  { value: 'Tourist', label: '旅游 Tourist' },
  { value: 'Visiting relatives', label: '探亲 Visiting relatives' },
  { value: 'Working', label: '工作 Working' },
  { value: 'Business', label: '商务 Business' },
  { value: 'Other', label: '其他 Other' },
];
const EXPENSE_SPONSORS = [
  { value: 'Personal', label: '本人承担' },
  { value: 'Company', label: '公司/组织承担' },
];
const YES_NO_OPTIONS = [
  { value: 'Yes', label: '是' },
  { value: 'No', label: '否' },
];

const STATUS_CN_MAP = {
  'pending': '等待中',
  'processing': '处理中',
  'waiting_captcha': '等待验证码',
  'completed': '已完成',
  'failed': '出现错误，请联系客服 lvbaobao@hongyi.space',
  'error': '出现错误，请联系客服 lvbaobao@hongyi.space',
};

const STEP_CN_MAP = {
  'waiting_captcha': '等待验证码',
  'filling_form': '填写表单中',
  'submitting': '提交中',
  'completed': '已完成',
};

const AIR_GATES = [
  { value: 'Tan Son Nhat Airport', label: '新山一机场（胡志明）' },
  { value: 'Noi Bai Airport', label: '内排机场（河内）' },
  { value: 'Da Nang Airport', label: '岘港机场（岘港）' },
  { value: 'Cam Ranh Airport', label: '金兰机场（芽庄）' },
  { value: 'Cat Bi Airport', label: '吉碑机场（海防）' },
  { value: 'Can Tho Airport', label: '芹苴机场（芹苴）' },
  { value: 'Phu Bai Airport', label: '富牌机场（顺化）' },
  { value: 'Phu Quoc Airport', label: '富国岛机场（坚江）' },
  { value: 'Van Don Airport', label: '云屯机场（广宁）' },
  { value: 'Tho Xuan Airport', label: '寿春机场（清化）' },
  { value: 'Dong Hoi Airport', label: '洞海机场（广平）' },
  { value: 'Phu Cat Airport', label: '富吉机场（平定）' },
  { value: 'Lien Khuong Airport', label: '连姜机场（大叻）' },
];

const LAND_GATES = [
  { value: 'Bo Y International Border Gate', label: '波也陆路口岸（昆嵩）' },
  { value: 'Cha Lo International Border Gate', label: '查萝口岸（广平）' },
  { value: 'Cau Treo International Border Gate', label: '高逗口岸（河静）' },
  { value: 'Huu Nghi International Border Gate', label: '友谊关（谅山）' },
  { value: 'Ha Tien International Border Gate', label: '河仙口岸（坚江）' },
  { value: 'Lao Bao International Border Gate', label: '老保口岸（广治）' },
  { value: 'Lao Cai International Border Gate', label: '老街口岸（老街）' },
  { value: 'La Lay International Border Gate', label: '拉莱口岸（广治）' },
  { value: 'Moc Bai International Border Gate', label: '木排口岸（西宁）' },
  { value: 'Mong Cai International Border Gate', label: '芒街口岸（广宁）' },
  { value: 'Nam Can International Border Gate', label: '南墩口岸（乂安）' },
  { value: 'Na Meo International Border Gate', label: '那帽口岸（清化）' },
  { value: 'Vinh Xuong International Land and River Gate', label: '永祥陆水路口岸（安江）' },
  { value: 'Tinh Bien International Border Gate', label: '靖边口岸（安江）' },
  { value: 'Tay Trang International Border Gate', label: '西庄口岸（奠边）' },
  { value: 'Xa Mat International Border Gate', label: '沙沫口岸（西宁）' },
];

const SEA_GATES = [
  { value: 'Chan May Port', label: '真梅港（顺化）' },
  { value: 'Cam Pha Port', label: '锦普港（广宁）' },
  { value: 'Da Nang Port', label: '岘港港（岘港）' },
  { value: 'Duong Dong Port', label: '阳东港（富国）' },
  { value: 'Dung Quat Port', label: '荣国港（广义）' },
  { value: 'Hon Gai Port', label: '红牌港（广宁）' },
  { value: 'Hai Phong Port', label: '海防港（海防）' },
  { value: 'Nghi Son Port', label: '义山港（清化）' },
  { value: 'Nha Trang Port', label: '芽庄港（庆和）' },
  { value: 'Quy Nhon Port', label: '归仁港（平定）' },
  { value: 'Ho Chi Minh City Port', label: '胡志明市港（胡志明）' },
  { value: 'Vung Ang Port', label: '荣安港（河静）' },
  { value: 'Vung Tau Port', label: '头顿港（巴地-头顿）' },
];

const BORDER_GATE_GROUPS = [
  { label: '✈️ 空港（Airports）', options: AIR_GATES },
  { label: '🚧 陆路口岸（Land）', options: LAND_GATES },
  { label: '⚓ 海港（Sea）', options: SEA_GATES },
];

// 省份中文映射表（用于API数据显示中文）
const PROVINCE_CN_MAP = {
  // 带 City 后缀的版本（匹配 API 返回格式）
  'Ha Noi City': '河内',
  'Ho Chi Minh City': '胡志明市',
  'Da Nang City': '岘港',
  'Hai Phong City': '海防',
  'Can Tho City': '芹苴',
  'Hue City': '承天顺化',
  // 不带 City 的版本（兼容 fallback 数据）
  'Ha Noi': '河内',
  'Hanoi': '河内',
  'Ho Chi Minh': '胡志明市',
  'Da Nang': '岘港',
  'Hai Phong': '海防',
  'Can Tho': '芹苴',
  // 其他省份
  'Binh Duong': '平阳',
  'Dong Nai': '同奈',
  'Khanh Hoa': '庆和',
  'Ba Ria - Vung Tau': '巴地-头顿',
  'Ba Ria-Vung Tau': '巴地-头顿',
  'Quang Ninh': '广宁',
  'Lam Dong': '林同',
  'Thua Thien Hue': '承天顺化',
  'Thua Thien - Hue': '承天顺化',
  'Quang Nam': '广南',
  'Binh Thuan': '平顺',
  'Ninh Binh': '宁平',
  'Quang Binh': '广平',
  'Nghe An': '乂安',
  'Ha Tinh': '河静',
  'Thanh Hoa': '清化',
  'Lao Cai': '老街',
  'Dien Bien': '奠边',
  'Son La': '山罗',
  'Hoa Binh': '和平',
  'Lang Son': '谅山',
  'Cao Bang': '高平',
  'Ha Giang': '河江',
  'Yen Bai': '安沛',
  'Phu Tho': '富寿',
  'Vinh Phuc': '永福',
  'Bac Ninh': '北宁',
  'Hai Duong': '海阳',
  'Hung Yen': '兴安',
  'Thai Binh': '太平',
  'Nam Dinh': '南定',
  'Ha Nam': '河南',
  'Tay Ninh': '西宁',
  'Binh Phuoc': '平福',
  'Long An': '隆安',
  'Tien Giang': '前江',
  'Ben Tre': '槟椥',
  'Tra Vinh': '茶荣',
  'Vinh Long': '永隆',
  'Dong Thap': '同塔',
  'An Giang': '安江',
  'Kien Giang': '坚江',
  'Hau Giang': '后江',
  'Soc Trang': '朔庄',
  'Bac Lieu': '薄辽',
  'Ca Mau': '金瓯',
  'Phu Yen': '富安',
  'Binh Dinh': '平定',
  'Quang Ngai': '广义',
  'Kon Tum': '昆嵩',
  'Gia Lai': '嘉莱',
  'Dak Lak': '得乐',
  'Dac Lac': '得乐',
  'Dak Nong': '得农',
  'Bac Giang': '北江',
  'Bac Kan': '北件',
  'Tuyen Quang': '宣光',
  'Thai Nguyen': '太原',
  'Lai Chau': '莱州',
  'Quang Tri': '广治',
  'Ninh Thuan': '宁顺',
};

// 口岸中文映射表（用于API数据显示中文）
const GATE_CN_MAP = {
  // ===== 空港 =====
  'Tan Son Nhat Int Airport (Ho Chi Minh City)': '新山一机场（胡志明）',
  'Tan Son Nhat International Airport': '新山一机场（胡志明）',
  'Tan Son Nhat Airport': '新山一机场（胡志明）',
  'Noi Bai Int Airport': '内排机场（河内）',
  'Noi Bai International Airport': '内排机场（河内）',
  'Noi Bai Airport': '内排机场（河内）',
  'Da Nang International Airport': '岘港机场（岘港）',
  'Da Nang Airport': '岘港机场（岘港）',
  'Cam Ranh Int Airport (Khanh Hoa)': '金兰机场（芽庄）',
  'Cam Ranh International Airport': '金兰机场（芽庄）',
  'Cam Ranh Airport': '金兰机场（芽庄）',
  'Cat Bi Int Airport (Hai Phong)': '吉碑机场（海防）',
  'Cat Bi International Airport': '吉碑机场（海防）',
  'Cat Bi Airport': '吉碑机场（海防）',
  'Can Tho International Airport': '芹苴机场（芹苴）',
  'Can Tho Airport': '芹苴机场（芹苴）',
  'Phu Bai Int Airport': '富牌机场（顺化）',
  'Phu Bai International Airport': '富牌机场（顺化）',
  'Phu Bai Airport': '富牌机场（顺化）',
  'Phu Quoc International Airport': '富国岛机场（坚江）',
  'Phu Quoc Airport': '富国岛机场（坚江）',
  'Van Don Int Airport': '云屯机场（广宁）',
  'Van Don International Airport': '云屯机场（广宁）',
  'Van Don Airport': '云屯机场（广宁）',
  'Lien Khuong International Airport': '连姜机场（大叻）',
  'Lien Khuong Airport': '连姜机场（大叻）',
  'Dong Hoi Int Airport (Quang Binh)': '洞海机场（广平）',
  'Dong Hoi Airport': '洞海机场（广平）',
  'Phu Cat Int Airport': '富吉机场（平定）',
  'Phu Cat Airport': '富吉机场（平定）',
  'Tho Xuan International Airport': '寿春机场（清化）',
  'Tho Xuan Airport': '寿春机场（清化）',
  // ===== 陆路口岸 =====
  'Huu Nghi Landport': '友谊关（谅山）',
  'Huu Nghi International Border Gate': '友谊关（谅山）',
  'Mong Cai Landport': '芒街口岸（广宁）',
  'Mong Cai International Border Gate': '芒街口岸（广宁）',
  'Lao Cai Landport': '老街口岸（老街）',
  'Lao Cai International Border Gate': '老街口岸（老街）',
  'Moc Bai Landport': '木排口岸（西宁）',
  'Moc Bai International Border Gate': '木排口岸（西宁）',
  'Lao Bao Landport': '老保口岸（广治）',
  'Lao Bao International Border Gate': '老保口岸（广治）',
  'Bo Y Landport': '波也口岸（昆嵩）',
  'Bo Y International Border Gate': '波也口岸（昆嵩）',
  'Cha Lo Landport': '查萝口岸（广平）',
  'Cha Lo International Border Gate': '查萝口岸（广平）',
  'Cau Treo Landport': '高逗口岸（河静）',
  'Cau Treo International Border Gate': '高逗口岸（河静）',
  'Ha Tien Landport': '河仙口岸（坚江）',
  'Ha Tien International Border Gate': '河仙口岸（坚江）',
  'La Lay Landport': '拉莱口岸（广治）',
  'La Lay International Border Gate': '拉莱口岸（广治）',
  'Nam Can Landport': '南墩口岸（乂安）',
  'Nam Can International Border Gate': '南墩口岸（乂安）',
  'Na Meo Landport': '那帽口岸（清化）',
  'Na Meo International Border Gate': '那帽口岸（清化）',
  'Vinh Xuong Landport': '永祥口岸（安江）',
  'Vinh Xuong International Land and River Gate': '永祥口岸（安江）',
  'Tinh Bien Landport': '靖边口岸（安江）',
  'Tinh Bien International Border Gate': '靖边口岸（安江）',
  'Tay Trang Landport': '西庄口岸（奠边）',
  'Tay Trang International Border Gate': '西庄口岸（奠边）',
  'Xa Mat Landport': '沙沫口岸（西宁）',
  'Xa Mat International Border Gate': '沙沫口岸（西宁）',
  // ===== 海港 =====
  'Da Nang Seaport': '岘港港（岘港）',
  'Da Nang Port': '岘港港（岘港）',
  'Hai Phong Seaport': '海防港（海防）',
  'Hai Phong Port': '海防港（海防）',
  'Nha Trang Seaport': '芽庄港（庆和）',
  'Nha Trang Port': '芽庄港（庆和）',
  'Ho Chi Minh City Seaport': '胡志明市港（胡志明）',
  'Ho Chi Minh City Port': '胡志明市港（胡志明）',
  'Vung Tau Seaport': '头顿港（巴地-头顿）',
  'Vung Tau Port': '头顿港（巴地-头顿）',
  'Chan May Seaport': '真梅港（顺化）',
  'Chan May Port': '真梅港（顺化）',
  'Cam Pha Seaport': '锦普港（广宁）',
  'Cam Pha Port': '锦普港（广宁）',
  'Duong Dong Seaport': '阳东港（富国）',
  'Duong Dong Port': '阳东港（富国）',
  'Dung Quat Seaport': '荣橘港（广义）',
  'Dung Quat Port': '荣橘港（广义）',
  'Hon Gai Seaport': '鸿基港（广宁）',
  'Hon Gai Port': '鸿基港（广宁）',
  'Nghi Son Seaport': '义山港（清化）',
  'Nghi Son Port': '义山港（清化）',
  'Quy Nhon Seaport': '归仁港（平定）',
  'Quy Nhon Port': '归仁港（平定）',
  'Vung Ang Seaport': '荣安港（河静）',
  'Vung Ang Port': '荣安港（河静）',
};

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const VN_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

// 检查是否包含非ASCII字符（中文等）
function containsNonAscii(text) {
  if (!text) return false;
  return /[^\x00-\x7F]/.test(text);
}

// 大小写不敏感的中文查找函数
function lookupChinese(map, key) {
  if (!key) return undefined;
  // 直接匹配
  if (map[key]) return map[key];
  // 大小写不敏感匹配
  const normalizedKey = key.toLowerCase().trim();
  for (const [mapKey, value] of Object.entries(map)) {
    if (mapKey.toLowerCase().trim() === normalizedKey) {
      return value;
    }
  }
  return undefined;
}

const FALLBACK_PROVINCE_OPTIONS = FALLBACK_VIETNAM_PROVINCES.map((item) => ({
  value: item.value,
  label: item.label,
}));
const FALLBACK_WARD_MAP = buildWardMapFromFallback(FALLBACK_VIETNAM_PROVINCES);

const GATE_CATEGORY_ORDER = [
  { key: 'air', label: '✈️ 空港（Airports）' },
  { key: 'land', label: '🚧 陆路口岸（Land）' },
  { key: 'sea', label: '⚓ 海港（Sea）' },
];

function buildWardMapFromFallback(provinces) {
  return provinces.reduce((acc, province) => {
    acc[province.value] = (province.wards || []).map((ward) =>
      typeof ward === 'string' ? { value: ward, label: ward } : ward
    );
    return acc;
  }, {});
}

function formatOptionLabel(en, vi) {
  if (!vi || vi === en) return en;
  return `${en} / ${vi}`;
}

function normalizeProvincesFromApi(data = []) {
  return data.map((province) => {
    const cn = lookupChinese(PROVINCE_CN_MAP, province.name_en);
    const enVi = formatOptionLabel(province.name_en, province.name_vi);
    return {
      value: province.name_en,
      label: cn ? `${cn} / ${enVi}` : enVi,
    };
  });
}

function buildWardMapFromApi(data = []) {
  return data.reduce((acc, province) => {
    acc[province.name_en] = (province.wards || []).map((ward) => ({
      value: ward.name_en,
      label: formatOptionLabel(ward.name_en, ward.name_vi),
    }));
    return acc;
  }, {});
}

function buildGateGroupsFromApi(payload) {
  if (!payload) return [];
  const combined = [...(payload.entry || []), ...(payload.exit || [])];
  const unique = new Map();
  combined.forEach((gate) => {
    const key = gate.code || gate.name_en;
    if (!key || unique.has(key)) return;
    unique.set(key, gate);
  });

  const grouped = new Map();
  unique.forEach((gate) => {
    const categoryKey = gate.category || 'other';
    if (!grouped.has(categoryKey)) {
      const meta = GATE_CATEGORY_ORDER.find((item) => item.key === categoryKey);
      grouped.set(categoryKey, {
        label: meta?.label || gate.category_label || categoryKey,
        options: [],
      });
    }
    const cn = lookupChinese(GATE_CN_MAP, gate.name_en);
    grouped.get(categoryKey).options.push({
      value: gate.name_en,
      label: cn
        ? `${cn} / ${formatOptionLabel(gate.name_en, gate.name_vi)}`
        : formatOptionLabel(gate.name_en, gate.name_vi),
    });
  });

  const ordered = [];
  GATE_CATEGORY_ORDER.forEach((meta) => {
    if (grouped.has(meta.key)) {
      ordered.push({
        label: meta.label,
        options: grouped.get(meta.key).options,
      });
      grouped.delete(meta.key);
    }
  });
  grouped.forEach((group) => ordered.push(group));
  return ordered;
}

function formatDateString(value) {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (VN_DATE_REGEX.test(trimmed)) {
    return trimmed;
  }
  if (ISO_DATE_REGEX.test(trimmed)) {
    const [year, month, day] = trimmed.split('-');
    return `${day}/${month}/${year}`;
  }
  return trimmed;
}

function normalizeDateArray(list, fields) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => {
    const next = { ...item };
    fields.forEach((field) => {
      if (next[field]) {
        next[field] = formatDateString(next[field]);
      }
    });
    return next;
  });
}

function normalizeDatePayload(data) {
  const clone = JSON.parse(JSON.stringify(data));
  if (clone.personal) {
    clone.personal.dateOfBirth = formatDateString(clone.personal.dateOfBirth);
    clone.personal.otherPassports = normalizeDateArray(clone.personal.otherPassports, ['dob']);
  }
  if (clone.requested) {
    clone.requested.validFrom = formatDateString(clone.requested.validFrom);
    clone.requested.validTo = formatDateString(clone.requested.validTo);
  }
  if (clone.passport) {
    clone.passport.issueDate = formatDateString(clone.passport.issueDate);
    clone.passport.expiryDate = formatDateString(clone.passport.expiryDate);
    clone.passport.otherPassports = normalizeDateArray(clone.passport.otherPassports, ['dob', 'issueDate', 'expiryDate']);
  }
  if (clone.trip) {
    clone.trip.intendedEntry = formatDateString(clone.trip.intendedEntry);
    clone.trip.visitHistory = normalizeDateArray(clone.trip.visitHistory, ['fromDate', 'toDate']);
    clone.trip.relatives = normalizeDateArray(clone.trip.relatives, ['dob']);
  }
  clone.accompanyChildren = normalizeDateArray(clone.accompanyChildren, ['dob']);
  return clone;
}

const initialFormData = {
  images: {
    portrait: null,
    passportPage: null,
  },
  personal: {
    surname: '',
    givenNames: '',
    dobMode: 'full',
    dateOfBirth: '',
    sex: '',
    nationality: 'China',
    identityCard: '',
    email: '',
    reEmail: '',
    religion: '',
    placeOfBirth: '',
    usedOtherPassports: false,
    otherPassports: [],
    multipleNationalities: false,
    otherNationalities: [],
    legalViolation: false,
    legalViolationNote: '',
  },
  requested: {
    issueFor: 'single',
    validFrom: '',
    validTo: '',
  },
  passport: {
    number: '',
    issuingAuthority: 'China National Immigration Administration',
    type: '',
    issueDate: '',
    expiryDate: '',
    holdsOtherPassports: false,
    otherPassports: [],
  },
  contact: {
    permanentAddress: '',
    contactAddress: '',
    phone: '',
    emergency: {
      fullName: '',
      address: '',
      phone: '',
      relation: '',
    },
  },
  occupation: {
    occupation: '',
    occupationInfo: '',
    companyName: '',
    position: '',
    companyAddress: '',
    companyPhone: '',
  },
  trip: {
    purpose: '',
    intendedEntry: '',
    lengthOfStay: '',
    phoneInVietnam: '',
    residentialAddress: '',
    province: '',
    ward: '',
    entryGate: '',
    exitGate: '',
    commitResidenceDeclaration: true,
    hasAgencyContact: false,
    agencyContacts: [],
    visitedInYear: false,
    visitHistory: [],
    hasRelatives: false,
    relatives: [],
  },
  accompanyChildren: [],
  expenses: {
    intendedExpenses: '',
    boughtInsurance: '',
    expenseSponsor: '',
  },
  declarations: {
    confirmLaws: true,
    confirmInstructions: true,
    finalDeclaration: true,
  },
};

const sections = [
  { key: 'images', title: '照片与护照资料页' },
  { key: 'personal', title: '个人信息' },
  { key: 'requested', title: '申请签证信息' },
  { key: 'passport', title: '护照信息' },
  { key: 'contact', title: '联系方式与紧急联系人' },
  { key: 'occupation', title: '职业信息' },
  { key: 'trip', title: '行程信息' },
  { key: 'accompanyChildren', title: '同行儿童' },
  { key: 'expenses', title: '费用与保险' },
  { key: 'declarations', title: '声明与提交' },
];

export default function VnEvisaForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [task, setTask] = useState(null);
  const [status, setStatus] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [detailTask, setDetailTask] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaImage, setCaptchaImage] = useState(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaSubmitting, setCaptchaSubmitting] = useState(false);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [wardMap, setWardMap] = useState({});
  const [borderGateOptions, setBorderGateOptions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(true);
  const [provinceError, setProvinceError] = useState('');
  const [gateError, setGateError] = useState('');

  const loadReferenceData = useCallback(async () => {
    setLocationLoading(true);
    setProvinceError('');
    setGateError('');
    const [locationsResult, gatesResult] = await Promise.allSettled([
      getVnEvisaLocations(),
      getVnEvisaBorderGates(),
    ]);

    if (locationsResult.status === 'fulfilled' && Array.isArray(locationsResult.value?.provinces) && locationsResult.value.provinces.length > 0) {
      const provinces = locationsResult.value.provinces;
      setProvinceOptions(normalizeProvincesFromApi(provinces));
      setWardMap(buildWardMapFromApi(provinces));
      setProvinceError('');
    } else {
      const reason =
        locationsResult.status === 'rejected'
          ? locationsResult.reason?.message || String(locationsResult.reason || '')
          : '省/市列表为空';
      setProvinceOptions(FALLBACK_PROVINCE_OPTIONS);
      setWardMap(FALLBACK_WARD_MAP);
      setProvinceError(reason || '省/市列表同步失败');
    }

    if (gatesResult.status === 'fulfilled' && (gatesResult.value?.entry?.length || gatesResult.value?.exit?.length)) {
      const groups = buildGateGroupsFromApi(gatesResult.value);
      setBorderGateOptions(groups.length ? groups : BORDER_GATE_GROUPS);
      setGateError(groups.length ? '' : '口岸列表为空');
    } else {
      const reason =
        gatesResult.status === 'rejected'
          ? gatesResult.reason?.message || String(gatesResult.reason || '')
          : '口岸列表为空';
      setBorderGateOptions(BORDER_GATE_GROUPS);
      setGateError(reason || '口岸列表同步失败');
    }

    setLocationLoading(false);
  }, []);

  const refreshTaskList = useCallback(async () => {
    try {
      const data = await listVnEvisaTasks(10);
      setTasks(data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    refreshTaskList();
  }, [refreshTaskList]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  const openTaskDetail = async (taskId) => {
    setDetailLoading(true);
    try {
      const resp = await getVnEvisaStatus(taskId);
      setDetailTask(resp);
    } catch (err) {
      console.error(err);
      setError(err.message || '获取状态失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const updateField = (sectionKey, fieldKey, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [fieldKey]: value,
      },
    }));
  };

  const updateNestedField = (sectionKey, nestedKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [nestedKey]: {
          ...prev[sectionKey][nestedKey],
          [field]: value,
        },
      },
    }));
  };

  const handleUpload = async (purpose, file) => {
    if (!file) return;
    setError('');
    try {
      const result = await uploadVnEvisaFile(file, purpose);
      setFormData((prev) => ({
        ...prev,
        images: {
          ...prev.images,
          [purpose]: result,
        },
      }));
    } catch (err) {
      console.error(err);
      setError(err.message || '上传失败，请重试');
    }
  };

  const payloadFileRefs = useMemo(() => {
    const refs = [];
    if (formData.images.portrait) refs.push({ ...formData.images.portrait, purpose: 'portrait' });
    if (formData.images.passportPage) refs.push({ ...formData.images.passportPage, purpose: 'passport' });
    formData.accompanyChildren.forEach((child, index) => {
      if (child.photo?.token) {
        refs.push({ ...child.photo, purpose: `child_${index}` });
      }
    });
    return refs.map((item) => ({
      token: item.token,
      purpose: item.purpose,
      filename: item.filename,
    }));
  }, [formData]);

  const validateForm = useCallback(() => {
    const issues = [];
    const push = (section, message) => issues.push({ section, message });
    const isEmpty = (value) => value === undefined || value === null || value === '';

    // 检查文本字段是否包含非ASCII字符
    const checkNonAscii = (value, fieldName, section) => {
      if (containsNonAscii(value)) {
        push(section, `"${fieldName}" 包含中文或其他非英文字符，请使用英文填写`);
      }
    };

    if (!formData.images.portrait) push('images', '上传个人证件照');
    if (!formData.images.passportPage) push('images', '上传护照资料页');

    const personal = formData.personal;
    if (isEmpty(personal.surname)) push('personal', '填写姓氏');
    if (isEmpty(personal.givenNames)) push('personal', '填写名字');
    if (isEmpty(personal.dateOfBirth)) push('personal', '填写出生日期');
    if (isEmpty(personal.sex)) push('personal', '选择性别');
    if (isEmpty(personal.nationality)) push('personal', '填写国籍');
    if (isEmpty(personal.email)) push('personal', '填写邮箱');
    if (isEmpty(personal.reEmail)) push('personal', '确认邮箱');
    if (!isEmpty(personal.email) && personal.email !== personal.reEmail) push('personal', '两次邮箱需一致');
    if (isEmpty(personal.religion)) push('personal', '填写宗教（可填 None）');
    if (isEmpty(personal.placeOfBirth)) push('personal', '填写出生地');

    // 个人信息字段的非ASCII检查
    checkNonAscii(personal.surname, '姓氏', 'personal');
    checkNonAscii(personal.givenNames, '名字', 'personal');
    checkNonAscii(personal.nationality, '国籍', 'personal');
    checkNonAscii(personal.religion, '宗教', 'personal');
    checkNonAscii(personal.placeOfBirth, '出生地', 'personal');

    const requested = formData.requested;
    if (isEmpty(requested.validFrom)) push('requested', '填写签证有效期起始');
    if (isEmpty(requested.validTo)) push('requested', '填写签证有效期结束');

    const passport = formData.passport;
    if (isEmpty(passport.number)) push('passport', '填写护照号');
    if (isEmpty(passport.type)) push('passport', '选择护照类型');
    if (isEmpty(passport.issueDate)) push('passport', '填写护照签发日');
    if (isEmpty(passport.expiryDate)) push('passport', '填写护照到期日');

    // 护照字段的非ASCII检查
    checkNonAscii(passport.number, '护照号', 'passport');
    checkNonAscii(passport.issuingAuthority, '签发机构', 'passport');

    const contact = formData.contact;
    if (isEmpty(contact.permanentAddress)) push('contact', '填写常住地址');
    if (isEmpty(contact.contactAddress)) push('contact', '填写联系地址');
    if (isEmpty(contact.phone)) push('contact', '填写联系电话');
    if (isEmpty(contact.emergency.fullName)) push('contact', '填写紧急联系人姓名');
    if (isEmpty(contact.emergency.address)) push('contact', '填写紧急联系人地址');
    if (isEmpty(contact.emergency.phone)) push('contact', '填写紧急联系人电话');
    if (isEmpty(contact.emergency.relation)) push('contact', '填写紧急联系人关系');

    // 联系方式字段的非ASCII检查
    checkNonAscii(contact.permanentAddress, '常住地址', 'contact');
    checkNonAscii(contact.contactAddress, '联系地址', 'contact');
    checkNonAscii(contact.emergency.fullName, '紧急联系人姓名', 'contact');
    checkNonAscii(contact.emergency.address, '紧急联系人地址', 'contact');
    checkNonAscii(contact.emergency.relation, '紧急联系人关系', 'contact');

    const trip = formData.trip;
    if (isEmpty(trip.purpose)) push('trip', '选择入境目的');
    if (isEmpty(trip.intendedEntry)) push('trip', '填写计划入境日');
    if (isEmpty(trip.lengthOfStay)) push('trip', '填写停留天数');
    if (isEmpty(trip.residentialAddress)) push('trip', '填写在越住址');
    if (isEmpty(trip.province)) push('trip', '填写省/市');
    if (isEmpty(trip.ward)) push('trip', '填写区/坊/社');
    if (isEmpty(trip.entryGate)) push('trip', '填写入境口岸');
    if (isEmpty(trip.exitGate)) push('trip', '填写出境口岸');
    if (!trip.commitResidenceDeclaration) push('trip', '勾选"承诺登记临时住宿"');

    // 行程字段的非ASCII检查
    checkNonAscii(trip.residentialAddress, '在越住址', 'trip');
    checkNonAscii(trip.phoneInVietnam, '在越联系电话', 'trip');

    const declarations = formData.declarations;
    if (!declarations.confirmLaws) push('declarations', '勾选“遵守越南法律”');
    if (!declarations.confirmInstructions) push('declarations', '勾选“已阅读填写说明”');
    if (!declarations.finalDeclaration) push('declarations', '勾选最终声明');

    return issues;
  }, [formData]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const missing = validateForm();
      if (missing.length) {
        setSubmitting(false);
        const sectionIndex = sections.findIndex((s) => s.key === missing[0].section);
        if (sectionIndex >= 0) setStep(sectionIndex);
        setError(`请先完成：${missing.map((item) => item.message).join('、')}`);
        return;
      }

      const normalizedForm = normalizeDatePayload(formData);
      const payload = {
        payload: normalizedForm,
        file_refs: payloadFileRefs,
      };
      const resp = await createVnEvisaApplication(payload);
      setTask(resp);
      setStatus(null);
      setStep(sections.length - 1);
      await refreshTaskList();
    } catch (err) {
      console.error(err);
      setError(err.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusRefresh = async (targetTaskId) => {
    const derivedTaskId = targetTaskId || task?.task_id || task?.taskId;
    if (!derivedTaskId) return;
    try {
      const resp = await getVnEvisaStatus(derivedTaskId);
      setStatus(resp);

      // 如果状态为等待验证码，自动获取验证码图片
      if (resp.requires_user_action && resp.processing_step === 'waiting_captcha') {
        await fetchCaptchaImage(derivedTaskId);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || '查询失败');
    }
  };

  // 重试失败的任务
  const [retryingTaskId, setRetryingTaskId] = useState(null);
  const handleRetry = async (taskId) => {
    setRetryingTaskId(taskId);
    setError('');
    try {
      const resp = await retryVnEvisaTask(taskId);
      // 刷新任务列表
      await refreshTaskList();
      // 如果当前正在查看这个任务，更新状态
      if (task?.task_id === taskId || task?.taskId === taskId) {
        setTask(resp);
        setStatus(null);
      }
      alert(resp.message || '重试已提交');
    } catch (err) {
      console.error('重试失败:', err);
      setError(err.message || '重试失败');
    } finally {
      setRetryingTaskId(null);
    }
  };

  // 获取验证码图片
  const fetchCaptchaImage = async (taskId) => {
    try {
      const resp = await getVnEvisaCaptchaImage(taskId);
      if (resp && resp.image) {
        setCaptchaImage(`data:image/png;base64,${resp.image}`);
      }
    } catch (err) {
      console.error('获取验证码图片失败:', err);
    }
  };

  // 提交验证码
  const handleCaptchaSubmit = async (taskId) => {
    if (!captchaInput.trim()) {
      setError('请输入验证码');
      return;
    }
    setCaptchaSubmitting(true);
    try {
      await submitVnEvisaCaptcha(taskId, { captcha_text: captchaInput });
      setCaptchaInput('');
      setCaptchaImage(null);
      setError('');
      // 刷新任务状态
      await handleStatusRefresh(taskId);
    } catch (err) {
      console.error('提交验证码失败:', err);
      setError(err.message || '提交验证码失败');
    } finally {
      setCaptchaSubmitting(false);
    }
  };

  // 轮询检查验证码状态（详情弹窗）
  useEffect(() => {
    let intervalId;
    const currentTaskId = detailTask?.task_id;
    if (currentTaskId && detailTask?.requires_user_action && detailTask?.processing_step === 'waiting_captcha') {
      // 立即获取一次验证码图片
      fetchCaptchaImage(currentTaskId);
      // 每 3 秒检查一次
      intervalId = setInterval(() => {
        fetchCaptchaImage(currentTaskId);
      }, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [detailTask?.task_id, detailTask?.requires_user_action, detailTask?.processing_step]);

  // 提交任务后自动轮询状态
  useEffect(() => {
    let intervalId;
    const currentTaskId = task?.task_id || task?.taskId;

    if (currentTaskId) {
      // 定义轮询函数
      const pollStatus = async () => {
        try {
          const resp = await getVnEvisaStatus(currentTaskId);
          setStatus(resp);

          // 如果状态为等待验证码，获取验证码图片
          if (resp.requires_user_action && resp.processing_step === 'waiting_captcha') {
            await fetchCaptchaImage(currentTaskId);
          }

          // 如果任务完成或失败，停止轮询
          if (['completed', 'failed', 'error'].includes(resp.status)) {
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        } catch (err) {
          console.error('轮询状态失败:', err);
        }
      };

      // 立即获取一次状态
      pollStatus();

      // 每 5 秒轮询一次
      intervalId = setInterval(pollStatus, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [task?.task_id, task?.taskId]);

  const currentSection = sections[step];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-emerald-700 text-sm">Vietnam e-Visa</p>
          <h1 className="text-3xl font-extrabold text-gray-900 mt-1">越南 e-Visa 表单（中文界面）</h1>
          <p className="text-gray-600 mt-2">
            该表单对照越南移民局官网 8 大版块，使用中文界面降低填写难度。填写完成后点击"提交并进入自动化"，AI 机器人将自动完成官网表单填写。整个过程需要 5-10 分钟，请保持在线等待验证码。
          </p>
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-amber-800 font-medium">
              ⚠️ 所有字段请使用英文字母填写。中文地址、姓名等中文内容请转换为拼音或英文。
            </p>
          </div>
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-blue-900 font-medium">
              隐私提示：本服务后台不保存任何越南E-Visa申请记录，请在完成后自行保存回执/确认页截图。
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all whitespace-nowrap ml-4"
        >
          ← 返回首页
        </button>
      </header>

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 md:col-span-4 lg:col-span-3">
          <nav className="space-y-3">
            {sections.map((item, index) => {
              const isActive = step === index;
              return (
                <button
                  key={item.key}
                  onClick={() => setStep(index)}
                  className={`w-full text-left p-4 rounded-3xl border transition-all duration-200 ${
                    isActive
                      ? 'bg-white/90 border-emerald-400 shadow-xl scale-[1.01]'
                      : 'bg-white/60 border-transparent hover:border-emerald-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${
                          isActive ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div>
                        <p className={`text-sm font-semibold ${isActive ? 'text-gray-900' : 'text-gray-600'}`}>
                          {item.title}
                        </p>
                      </div>
                    </div>
                    {isActive && <span className="text-emerald-500 text-xs font-semibold">当前</span>}
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="col-span-12 md:col-span-8 lg:col-span-9">
          <div className="bg-white rounded-3xl border border-emerald-50 shadow-sm p-6 space-y-6">
            <div>
              <p className="text-xs uppercase text-emerald-600 font-semibold">第 {step + 1} 部分</p>
              <h2 className="text-2xl font-bold text-gray-900 mt-1">{currentSection.title}</h2>
            </div>
            <SectionRenderer
              section={currentSection.key}
              data={formData}
              updateField={updateField}
              updateNestedField={updateNestedField}
              setFormData={setFormData}
              handleUpload={handleUpload}
              provinceOptions={provinceOptions}
              wardMap={wardMap}
              borderGateOptions={borderGateOptions}
              locationLoading={locationLoading}
              provinceError={provinceError}
              gateError={gateError}
            />
            {currentSection.key !== 'declarations' && (
              <div className="flex justify-between">
                <button
                  onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
                  disabled={step === 0}
                  className="px-6 py-3 rounded-full border border-gray-200 text-gray-700 disabled:opacity-50"
                >
                  上一部分
                </button>
                <button
                  onClick={() => setStep((prev) => Math.min(prev + 1, sections.length - 1))}
                  className="px-6 py-3 rounded-full bg-emerald-500 text-white hover:bg-emerald-600"
                >
                  下一部分
                </button>
              </div>
            )}
            {currentSection.key === 'declarations' && (
              <div className="space-y-4">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full px-6 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-lime-500 text-white font-semibold shadow-lg disabled:opacity-50"
                >
                  {submitting ? '提交中...' :
                    formData.requested.issueFor === 'multiple'
                      ? '提交并进入自动化（400积分）'
                      : '提交并进入自动化（200积分）'}
                </button>
                {task && (
                  <div className="border border-emerald-100 rounded-2xl p-4 bg-emerald-50 text-sm text-gray-700">
                    <p>任务已创建，ID：{task.task_id || task.taskId}</p>
                    {status && (
                      <div className="mt-2 space-y-3 text-gray-800">
                        <p className={['failed', 'error'].includes(status.status) ? 'text-red-600 font-semibold' : ''}>
                          状态：{STATUS_CN_MAP[status.status] || status.status}
                        </p>
                        <p>阶段：{STEP_CN_MAP[status.processing_step] || status.processing_step || '--'}</p>

                        {/* 验证码输入区域 */}
                        {status.requires_user_action && status.processing_step === 'waiting_captcha' && (
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                            <p className="font-semibold text-amber-700">需要输入验证码</p>
                            {captchaImage ? (
                              <div className="space-y-3">
                                <div className="flex justify-center">
                                  <img src={captchaImage} alt="验证码" className="border rounded-lg" style={{ maxHeight: '80px' }} />
                                </div>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={captchaInput}
                                    onChange={(e) => setCaptchaInput(e.target.value)}
                                    placeholder="请输入验证码"
                                    className="flex-1 px-3 py-2 border rounded-lg"
                                    maxLength={10}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !captchaSubmitting) {
                                        handleCaptchaSubmit(task.task_id || task.taskId);
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={() => handleCaptchaSubmit(task.task_id || task.taskId)}
                                    disabled={captchaSubmitting || !captchaInput.trim()}
                                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:bg-gray-300"
                                  >
                                    {captchaSubmitting ? '提交中...' : '提交'}
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => fetchCaptchaImage(task.task_id || task.taskId)}
                                  className="text-sm text-emerald-600 hover:text-emerald-700"
                                >
                                  刷新验证码
                                </button>
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <div className="animate-spin h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-2 rounded-full"></div>
                                <p className="text-sm text-gray-500">正在加载验证码...</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {error && <div className="text-red-600 text-sm">{error}</div>}
          </div>
          {tasks?.length > 0 && (
            <section className="mt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">我的 e-Visa 任务</h3>
              <div className="border border-emerald-100 rounded-3xl p-4 bg-emerald-50/40 space-y-4">
            {tasks.map((item) => (
              <div key={item.task_id} className="bg-white rounded-2xl border border-emerald-100 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">任务 ID</p>
                        <p className="font-semibold text-gray-900">{item.task_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">状态</p>
                        <p className={`font-semibold ${['failed', 'error'].includes(item.status) ? 'text-red-600' : 'text-emerald-700'}`}>
                          {STATUS_CN_MAP[item.status] || item.status}
                        </p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mt-3 text-sm text-gray-600">
                      <div>
                        <p>创建时间：{new Date(item.created_at).toLocaleString()}</p>
                        <p>更新时间：{new Date(item.updated_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <p>阶段：{item.processing_step || '—'}</p>
                        <p>支付状态：{item.payment_status || '未扣费'}</p>
                      </div>
                    </div>
                <p className="mt-2 text-gray-700">说明：{item.message || '--'}</p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => openTaskDetail(item.task_id)}
                    className="px-4 py-2 text-sm text-emerald-600 font-semibold"
                    disabled={detailLoading && detailTask?.task_id === item.task_id}
                  >
                    {detailLoading && detailTask?.task_id === item.task_id ? '加载中…' : '查看详情'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStatusRefresh(item.task_id)}
                    className="px-4 py-2 text-sm text-gray-600"
                  >
                    刷新状态
                  </button>
                  {item.status === 'failed' && (
                    <button
                      type="button"
                      onClick={() => handleRetry(item.task_id)}
                      className="px-4 py-2 text-sm text-white bg-amber-500 hover:bg-amber-600 rounded-lg font-semibold"
                      disabled={retryingTaskId === item.task_id}
                    >
                      {retryingTaskId === item.task_id ? '重试中…' : '重试'}
                    </button>
                  )}
                </div>
              </div>
            ))}
                <button
                  type="button"
                  onClick={refreshTaskList}
                  className="px-4 py-2 text-emerald-600 font-semibold"
                >
                  刷新列表
                </button>
              </div>
            </section>
          )}
        </main>
      </div>
      {detailTask && (
        <div className="fixed inset-0 bg-gray-900/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 space-y-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">任务详情</h3>
              <button onClick={() => { setDetailTask(null); setCaptchaImage(null); setCaptchaInput(''); }} className="text-gray-500">✕</button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <p>任务 ID：{detailTask.task_id}</p>
                <p>状态：{detailTask.status}</p>
                <p>阶段：{detailTask.processing_step || '—'}</p>
                <p>支付状态：{detailTask.payment_status || '未扣费'}</p>
              </div>
              <div>
                <p>创建时间：{new Date(detailTask.created_at).toLocaleString()}</p>
                <p>更新时间：{new Date(detailTask.updated_at).toLocaleString()}</p>
                {detailTask.files_attached !== undefined && (
                  <p>已上传文件：{detailTask.files_attached}</p>
                )}
              </div>
            </div>

            {/* 验证码区域 */}
            {detailTask.requires_user_action && detailTask.processing_step === 'waiting_captcha' && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
                <p className="font-semibold text-amber-700">⚠️ 需要输入验证码</p>
                {captchaImage ? (
                  <div className="space-y-3">
                    <div className="flex justify-center">
                      <img
                        src={captchaImage}
                        alt="验证码"
                        className="border border-gray-300 rounded-lg max-w-full h-auto"
                        style={{ maxHeight: '80px' }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={captchaInput}
                        onChange={(e) => setCaptchaInput(e.target.value)}
                        placeholder="请输入验证码"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        maxLength={10}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !captchaSubmitting) {
                            handleCaptchaSubmit(detailTask.task_id);
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleCaptchaSubmit(detailTask.task_id)}
                        disabled={captchaSubmitting || !captchaInput.trim()}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {captchaSubmitting ? '提交中...' : '提交'}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => fetchCaptchaImage(detailTask.task_id)}
                      className="text-sm text-emerald-600 hover:text-emerald-700"
                    >
                      刷新验证码
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-2"></div>
                    <p className="text-gray-500 text-sm">正在加载验证码...</p>
                  </div>
                )}
              </div>
            )}

            {detailTask.result_snapshot && (
              <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-2xl">
                <p className="font-semibold">回执信息</p>
                <p>
                  申请号：
                  {detailTask.result_snapshot.application_code_hint || '尚未生成'}
                </p>
                {detailTask.result_snapshot.result_screenshot && (
                  <p className="text-xs text-gray-500 mt-1">
                    结果截图：{detailTask.result_snapshot.result_screenshot}
                  </p>
                )}
              </div>
            )}
            <p className="text-gray-700">提示：{detailTask.message || '—'}</p>
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={async () => {
                  setDetailLoading(true);
                  try {
                    const resp = await getVnEvisaStatus(detailTask.task_id);
                    setDetailTask(resp);
                    if (resp.requires_user_action && resp.processing_step === 'waiting_captcha') {
                      await fetchCaptchaImage(detailTask.task_id);
                    }
                  } catch (err) {
                    console.error(err);
                  } finally {
                    setDetailLoading(false);
                  }
                }}
                disabled={detailLoading}
                className="px-4 py-2 text-emerald-600 font-semibold disabled:opacity-50"
              >
                {detailLoading ? '刷新中...' : '刷新状态'}
              </button>
              <button
                type="button"
                onClick={() => { setDetailTask(null); setCaptchaImage(null); setCaptchaInput(''); }}
                className="px-4 py-2 rounded-full bg-emerald-500 text-white"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionRenderer({
  section,
  data,
  updateField,
  updateNestedField,
  setFormData,
  handleUpload,
  provinceOptions,
  wardMap,
  borderGateOptions,
  locationLoading,
  provinceError,
  gateError,
}) {
  switch (section) {
    case 'images':
      return <ImagesSection images={data.images} onUpload={handleUpload} />;
    case 'personal':
      return (
        <PersonalSection
          data={data.personal}
          onChange={(field, value) => updateField('personal', field, value)}
          onTableChange={(field, rows) =>
            setFormData((prev) => ({
              ...prev,
              personal: { ...prev.personal, [field]: rows },
            }))
          }
        />
      );
    case 'requested':
      return (
        <RequestedSection data={data.requested} onChange={(field, value) => updateField('requested', field, value)} />
      );
    case 'passport':
      return (
        <PassportSection
          data={data.passport}
          onChange={(field, value) => updateField('passport', field, value)}
          onTableChange={(field, rows) =>
            setFormData((prev) => ({
              ...prev,
              passport: { ...prev.passport, [field]: rows },
            }))
          }
        />
      );
    case 'contact':
      return (
        <ContactSection
          data={data.contact}
          onChange={(field, value) => updateField('contact', field, value)}
          onEmergencyChange={(field, value) => updateNestedField('contact', 'emergency', field, value)}
        />
      );
    case 'occupation':
      return (
        <OccupationSection
          data={data.occupation}
          onChange={(field, value) => updateField('occupation', field, value)}
        />
      );
    case 'trip':
      return (
        <TripSection
          data={data.trip}
          onChange={(field, value) => updateField('trip', field, value)}
          onTableChange={(field, rows) =>
            setFormData((prev) => ({
              ...prev,
              trip: { ...prev.trip, [field]: rows },
            }))
          }
          provinceOptions={provinceOptions}
          wardMap={wardMap}
          borderGateOptions={borderGateOptions}
          locationLoading={locationLoading}
          provinceError={provinceError}
          gateError={gateError}
        />
      );
    case 'accompanyChildren':
      return (
        <ChildrenSection
          rows={data.accompanyChildren}
          setRows={(rows) =>
            setFormData((prev) => ({
              ...prev,
              accompanyChildren: rows,
            }))
          }
          onUpload={handleUpload}
        />
      );
    case 'expenses':
      return (
        <ExpensesSection
          data={data.expenses}
          onChange={(field, value) => updateField('expenses', field, value)}
        />
      );
    case 'declarations':
      return (
        <DeclarationsSection
          data={data.declarations}
          onChange={(field, value) => updateField('declarations', field, value)}
        />
      );
    default:
      return null;
  }
}

function SectionCard({ title, description, children }) {
  return (
    <div className="border border-emerald-100 rounded-2xl p-5 space-y-4 bg-emerald-50/30">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
        {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
      </div>
      <div className="grid gap-4">{children}</div>
    </div>
  );
}

function FormRow({ label, required, children, help }) {
  return (
    <label className="text-sm text-gray-700">
      <span className="font-medium text-gray-900">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
      {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
    </label>
  );
}

function TextInput({ value, onChange, className, ...props }) {
  const hasNonAscii = containsNonAscii(value);
  return (
    <div>
      <input
        value={value}
        onChange={onChange}
        {...props}
        className={`w-full border rounded-xl px-3 py-2 ${hasNonAscii ? 'border-red-500 bg-red-50' : ''} ${className || ''}`}
      />
      {hasNonAscii && (
        <p className="text-red-500 text-xs mt-1">请使用英文字母填写，不支持中文</p>
      )}
    </div>
  );
}

function SelectInput({ options, placeholder = '请选择', ...props }) {
  const normalized = (options || []).map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );
  return (
    <select {...props} className="w-full border rounded-xl px-3 py-2 text-sm">
      <option value="">{placeholder}</option>
      {normalized.map((opt) =>
        Array.isArray(opt.options) ? (
          <optgroup label={opt.label} key={opt.label}>
            {opt.options.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </optgroup>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        )
      )}
    </select>
  );
}

function RadioGroup({ value, options, onChange, name }) {
  return (
    <div className="flex flex-wrap gap-4">
      {options.map((opt) => (
        <label key={opt.value} className="inline-flex items-center gap-2">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={(e) => onChange(e.target.value)}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function Checkbox({ checked, onChange, label, disabled }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <span>{label}</span>
    </label>
  );
}

function ImagesSection({ images, onUpload }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <FileUploadCard
        title="证件照（必传）"
        description="近 6 个月白底正面照，4x6cm，JPG/JPEG，≤2MB"
        fileInfo={images.portrait}
        onFileSelect={(file) => onUpload('portrait', file)}
      />
      <FileUploadCard
        title="护照资料页（必传）"
        description="需清晰完整，四角可见，JPG/JPEG，≤2MB"
        fileInfo={images.passportPage}
        onFileSelect={(file) => onUpload('passportPage', file)}
      />
    </div>
  );
}

function FileUploadCard({ title, description, hint, fileInfo, onFileSelect }) {
  return (
    <div className="border border-emerald-100 rounded-2xl p-5 bg-white space-y-3">
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
      <input type="file" accept="image/*" onChange={(e) => onFileSelect(e.target.files?.[0])} />
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {fileInfo && (
        <div className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
          <p>✓ 上传成功：{fileInfo.filename}</p>
        </div>
      )}
    </div>
  );
}

function PersonalSection({ data, onChange, onTableChange }) {
  return (
    <div className="space-y-6">
      <SectionCard title="基本身份信息">
        <FormRow label="姓（Surname）" required>
          <TextInput value={data.surname} onChange={(e) => onChange('surname', e.target.value)} />
        </FormRow>
        <FormRow label="名（Given name）" required>
          <TextInput value={data.givenNames} onChange={(e) => onChange('givenNames', e.target.value)} />
        </FormRow>
        <FormRow label="出生日期" required>
          <TextInput type="date" value={data.dateOfBirth} onChange={(e) => onChange('dateOfBirth', e.target.value)} />
        </FormRow>
        <FormRow label="性别" required>
          <SelectInput value={data.sex} onChange={(e) => onChange('sex', e.target.value)} options={GENDER} />
        </FormRow>
        <FormRow label="国籍" required>
          <TextInput value={data.nationality} onChange={(e) => onChange('nationality', e.target.value)} />
        </FormRow>
        <FormRow label="身份证号码（如有）">
          <TextInput value={data.identityCard} onChange={(e) => onChange('identityCard', e.target.value)} />
        </FormRow>
        <FormRow label="邮箱" required>
          <TextInput value={data.email} onChange={(e) => onChange('email', e.target.value)} />
        </FormRow>
        <FormRow label="确认邮箱" required>
          <TextInput value={data.reEmail} onChange={(e) => onChange('reEmail', e.target.value)} />
        </FormRow>
        <FormRow label="宗教" required help="常用选项：None=无 / Buddhism=佛教 / Taoism=道教 / Islam=伊斯兰教 / Catholicism=天主教 / Christianity=基督教">
          <TextInput value={data.religion} onChange={(e) => onChange('religion', e.target.value)} />
        </FormRow>
        <FormRow label="出生地（城市/省份）" required>
          <TextInput value={data.placeOfBirth} onChange={(e) => onChange('placeOfBirth', e.target.value)} />
        </FormRow>
      </SectionCard>

      <SectionCard title="其他护照 / 多国籍 / 违规记录">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <p className="text-amber-700 text-sm">⚠️ 建议不要填写以下信息，以免引起不必要的麻烦</p>
        </div>
        <ToggleTable
          label="曾经使用其他护照入境越南？"
          checked={data.usedOtherPassports}
          onToggle={(val) => onChange('usedOtherPassports', val)}
          rows={data.otherPassports}
          onRowsChange={(rows) => onTableChange('otherPassports', rows)}
          columns={[
            { key: 'passport', label: '护照号' },
            { key: 'fullName', label: '姓名' },
            { key: 'dob', label: '出生日期', type: 'date' },
            { key: 'nationality', label: '国籍' },
          ]}
        />

        <ToggleTable
          label="是否拥有多重国籍？"
          checked={data.multipleNationalities}
          onToggle={(val) => onChange('multipleNationalities', val)}
          rows={data.otherNationalities}
          onRowsChange={(rows) => onTableChange('otherNationalities', rows)}
          columns={[{ key: 'nationality', label: '国籍' }]}
        />

        <FormRow label="是否违反过越南法律？">
          <div className="space-y-2">
            <RadioGroup
              name="violation"
              value={data.legalViolation ? 'yes' : 'no'}
              onChange={(val) => onChange('legalViolation', val === 'yes')}
              options={[
                { value: 'no', label: '否' },
                { value: 'yes', label: '是' },
              ]}
            />
            {data.legalViolation && (
              <TextInput
                placeholder="请用英文描述违规情况"
                value={data.legalViolationNote}
                onChange={(e) => onChange('legalViolationNote', e.target.value)}
              />
            )}
          </div>
        </FormRow>
      </SectionCard>
    </div>
  );
}

function RequestedSection({ data, onChange }) {
  return (
    <div className="space-y-6">
      <SectionCard title="签证类型">
        <FormRow label="申请 e-Visa 类型" required>
          <RadioGroup
            name="visaType"
            value={data.issueFor}
            onChange={(val) => onChange('issueFor', val)}
            options={[
              { value: 'single', label: '单次入境（200积分）90天有效' },
              { value: 'multiple', label: '多次入境（400积分）90天有效' },
            ]}
          />
        </FormRow>
      </SectionCard>
      <SectionCard title="拟入境区间">
        <FormRow label="签证有效期起始（Grant e-Visa valid from）" required>
          <TextInput type="date" value={data.validFrom} onChange={(e) => onChange('validFrom', e.target.value)} />
        </FormRow>
        <FormRow label="结束日期（To）" required>
          <TextInput type="date" value={data.validTo} onChange={(e) => onChange('validTo', e.target.value)} />
        </FormRow>
      </SectionCard>
    </div>
  );
}

function PassportSection({ data, onChange, onTableChange }) {
  return (
    <div className="space-y-6">
      <SectionCard title="护照信息">
        <FormRow label="护照号码" required>
          <TextInput value={data.number} onChange={(e) => onChange('number', e.target.value)} />
        </FormRow>
        <FormRow label="签发机构 / 地点">
          <TextInput value={data.issuingAuthority} onChange={(e) => onChange('issuingAuthority', e.target.value)} />
        </FormRow>
        <FormRow label="护照类型" required>
          <SelectInput value={data.type} onChange={(e) => onChange('type', e.target.value)} options={PASSPORT_TYPES} />
        </FormRow>
        <FormRow label="签发日期" required>
          <TextInput type="date" value={data.issueDate} onChange={(e) => onChange('issueDate', e.target.value)} />
        </FormRow>
        <FormRow label="到期日" required>
          <TextInput type="date" value={data.expiryDate} onChange={(e) => onChange('expiryDate', e.target.value)} />
        </FormRow>
      </SectionCard>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
        <p className="text-amber-700 text-sm">⚠️ 建议不要填写以下信息，以免引起不必要的麻烦</p>
      </div>
      <ToggleTable
        label="是否还有其他有效护照？"
        checked={data.holdsOtherPassports}
        onToggle={(val) => onChange('holdsOtherPassports', val)}
        rows={data.otherPassports}
        onRowsChange={(rows) => onTableChange('otherPassports', rows)}
        columns={[
          { key: 'passport', label: '护照号' },
          { key: 'fullName', label: '姓名' },
          { key: 'dob', label: '出生日期', type: 'date' },
          { key: 'nationality', label: '国籍' },
        ]}
      />
    </div>
  );
}

function ContactSection({ data, onChange, onEmergencyChange }) {
  return (
    <div className="space-y-6">
      <SectionCard title="联系地址">
        <FormRow label="常住地址" required help="地址可以用拼音填写">
          <TextInput value={data.permanentAddress} onChange={(e) => onChange('permanentAddress', e.target.value)} />
        </FormRow>
        <FormRow label="当前联系地址" required help="地址可以用拼音填写">
          <TextInput value={data.contactAddress} onChange={(e) => onChange('contactAddress', e.target.value)} />
        </FormRow>
        <FormRow label="联系电话" required>
          <TextInput value={data.phone} onChange={(e) => onChange('phone', e.target.value)} />
        </FormRow>
      </SectionCard>

      <SectionCard title="紧急联系人">
        <FormRow label="姓名" required help="姓名可以用拼音填写">
          <TextInput value={data.emergency.fullName} onChange={(e) => onEmergencyChange('fullName', e.target.value)} />
        </FormRow>
        <FormRow label="现居地址" required help="地址可以用拼音填写">
          <TextInput value={data.emergency.address} onChange={(e) => onEmergencyChange('address', e.target.value)} />
        </FormRow>
        <FormRow label="联系电话" required>
          <TextInput value={data.emergency.phone} onChange={(e) => onEmergencyChange('phone', e.target.value)} />
        </FormRow>
        <FormRow label="与申请人关系" required help="常用选项：Parents=父母 / Spouse=配偶 / Children=子女 / Friend=朋友">
          <TextInput value={data.emergency.relation} onChange={(e) => onEmergencyChange('relation', e.target.value)} />
        </FormRow>
      </SectionCard>
    </div>
  );
}

function OccupationSection({ data, onChange }) {
  return (
    <SectionCard title="职业信息">
      <FormRow label="职业">
        <SelectInput value={data.occupation} onChange={(e) => onChange('occupation', e.target.value)} options={OCCUPATIONS} />
      </FormRow>
      <FormRow label="职业描述" help="简要描述即可，不宜过多">
        <TextInput value={data.occupationInfo} onChange={(e) => onChange('occupationInfo', e.target.value)} />
      </FormRow>
      <FormRow label="公司 / 机构 / 学校名称">
        <TextInput value={data.companyName} onChange={(e) => onChange('companyName', e.target.value)} />
      </FormRow>
      <FormRow label="职位 / 学习课程">
        <TextInput value={data.position} onChange={(e) => onChange('position', e.target.value)} />
      </FormRow>
      <FormRow label="单位地址">
        <TextInput value={data.companyAddress} onChange={(e) => onChange('companyAddress', e.target.value)} />
      </FormRow>
      <FormRow label="单位电话">
        <TextInput value={data.companyPhone} onChange={(e) => onChange('companyPhone', e.target.value)} />
      </FormRow>
    </SectionCard>
  );
}

function TripSection({
  data,
  onChange,
  onTableChange,
  provinceOptions,
  wardMap,
  borderGateOptions,
  locationLoading,
  provinceError,
  gateError,
}) {
  const wardOptions = useMemo(() => {
    if (!data.province) return [];
    return wardMap[data.province] || [];
  }, [data.province, wardMap]);

  const handleProvinceChange = useCallback(
    (value) => {
      onChange('province', value);
      onChange('ward', '');
    },
    [onChange]
  );

  const handleWardChange = useCallback(
    (value) => {
      onChange('ward', value);
    },
    [onChange]
  );

  const provinceDisabled = provinceOptions.length === 0;
  const provinceHelp = provinceError
    ? `获取官方省/市列表失败，已回退到内置数据。${provinceError}`
    : provinceDisabled && locationLoading
    ? '正在加载官方省/市列表...'
    : undefined;
  const wardHelp = !data.province
    ? '请先选择省/市'
    : provinceError
    ? 'Ward / commune 列表基于备用数据，提交前请核对拼写。'
    : undefined;
  const gateDisabled = borderGateOptions.length === 0;
  const gateBaseHelp = '下拉列表按空港 / 陆路 / 海港分组，方便查找例如“内排机场（河内）”。';
  const entryGateHelp = gateError
    ? `${gateBaseHelp}（已回退：${gateError}）`
    : gateDisabled && locationLoading
    ? `${gateBaseHelp}（正在加载官方列表...）`
    : gateBaseHelp;
  const exitGateHelp =
    gateError && gateDisabled
      ? `口岸列表加载失败，已使用备用数据。${gateError}`
    : gateDisabled && locationLoading
    ? '口岸列表正在加载，请稍候。'
    : gateError && !gateDisabled
      ? `当前使用备用口岸数据：${gateError}`
      : undefined;

  return (
    <div className="space-y-6">
      <SectionCard title="行程信息">
        <FormRow label="入境目的" required>
          <SelectInput value={data.purpose} onChange={(e) => onChange('purpose', e.target.value)} options={PURPOSES} />
        </FormRow>
        <FormRow label="计划入境日期" required>
          <TextInput type="date" value={data.intendedEntry} onChange={(e) => onChange('intendedEntry', e.target.value)} />
        </FormRow>
        <FormRow label="计划停留时长（天）" required>
          <TextInput value={data.lengthOfStay} onChange={(e) => onChange('lengthOfStay', e.target.value)} />
        </FormRow>
        <FormRow label="在越联系电话">
          <TextInput value={data.phoneInVietnam} onChange={(e) => onChange('phoneInVietnam', e.target.value)} />
        </FormRow>
        <FormRow label="在越入住地址" required>
          <TextInput value={data.residentialAddress} onChange={(e) => onChange('residentialAddress', e.target.value)} />
        </FormRow>
        <FormRow label="省/市" required help={provinceHelp}>
          <SelectInput
            value={data.province}
            onChange={(e) => handleProvinceChange(e.target.value)}
            options={provinceOptions}
            placeholder={provinceDisabled ? '正在加载官方省/市列表...' : '请选择省/市'}
            disabled={provinceDisabled}
          />
        </FormRow>
        <FormRow label="区/坊/社（Ward / commune）" required help={wardHelp}>
          <SelectInput
            value={data.ward}
            onChange={(e) => handleWardChange(e.target.value)}
            options={wardOptions}
            disabled={!data.province || wardOptions.length === 0}
            placeholder={
              !data.province
                ? '请先选择省/市'
                : wardOptions.length === 0
                ? '该省暂无可选 ward 数据'
                : '请选择区/坊/社'
            }
          />
        </FormRow>
        <FormRow label="拟入境口岸" required help={entryGateHelp}>
          <SelectInput
            value={data.entryGate}
            onChange={(e) => onChange('entryGate', e.target.value)}
            options={borderGateOptions}
            disabled={gateDisabled}
            placeholder={gateDisabled ? '正在加载官方口岸列表...' : '请选择入境口岸'}
          />
        </FormRow>
        <FormRow label="拟出境口岸" required help={exitGateHelp}>
          <SelectInput
            value={data.exitGate}
            onChange={(e) => onChange('exitGate', e.target.value)}
            options={borderGateOptions}
            disabled={gateDisabled}
            placeholder={gateDisabled ? '正在加载官方口岸列表...' : '请选择出境口岸'}
          />
        </FormRow>
        <Checkbox
          checked={data.commitResidenceDeclaration}
          onChange={(val) => onChange('commitResidenceDeclaration', val)}
          disabled={true}
          label="承诺入境后按越南法律登记临时住宿"
        />
      </SectionCard>

      <ToggleTable
        label="在越将联系的机构/个人？"
        checked={data.hasAgencyContact}
        onToggle={(val) => onChange('hasAgencyContact', val)}
        rows={data.agencyContacts}
        onRowsChange={(rows) => onTableChange('agencyContacts', rows)}
        columns={[
          { key: 'name', label: '名称' },
          { key: 'phone', label: '电话' },
          { key: 'address', label: '地址' },
          { key: 'purpose', label: '来往目的' },
        ]}
      />

      <ToggleTable
        label="过去一年是否到访过越南？"
        checked={data.visitedInYear}
        onToggle={(val) => onChange('visitedInYear', val)}
        rows={data.visitHistory}
        onRowsChange={(rows) => onTableChange('visitHistory', rows)}
        columns={[
          { key: 'fromDate', label: '入境日期', type: 'date' },
          { key: 'toDate', label: '离境日期', type: 'date' },
          { key: 'purpose', label: '事由' },
        ]}
      />

      <ToggleTable
        label="是否有亲属目前居住在越南？"
        checked={data.hasRelatives}
        onToggle={(val) => onChange('hasRelatives', val)}
        rows={data.relatives}
        onRowsChange={(rows) => onTableChange('relatives', rows)}
        columns={[
          { key: 'fullName', label: '姓名' },
          { key: 'dob', label: '出生日期', type: 'date' },
          { key: 'nationality', label: '国籍' },
          { key: 'relationship', label: '关系' },
          { key: 'address', label: '在越地址' },
        ]}
      />
    </div>
  );
}

function ChildrenSection({ rows, setRows, onUpload }) {
  const handleFile = async (index, file) => {
    if (!file) return;
    const upload = await uploadVnEvisaFile(file, `child_${index}`);
    setRows(rows.map((row, idx) => (idx === index ? { ...row, photo: upload } : row)));
  };

  const hasChildren = rows && rows.length > 0;

  const toggleChildren = (checked) => {
    if (!checked) {
      setRows([]);
    } else if (!hasChildren) {
      setRows([{ fullName: '', sex: '', dob: '' }]);
    }
  };

  return (
    <SectionCard
      title="14岁以下同行儿童"
    >
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <span className="text-yellow-600 text-xl">⚠️</span>
          <div className="flex-1">
            <p className="text-yellow-800 font-semibold mb-2">重要提示</p>
            <p className="text-yellow-700 text-sm mb-2">
              此页面<strong>仅适用于与您共用同一本护照</strong>的 14 岁以下儿童。
            </p>
            <p className="text-yellow-700 text-sm">
              <strong>绝大部分儿童已有独立护照</strong>，如果您的孩子有自己的护照，请<strong>不要勾选下方复选框</strong>，直接为孩子单独申请 e-Visa。
            </p>
          </div>
        </div>
      </div>
      <Checkbox checked={hasChildren} onChange={toggleChildren} label="我有与我同护照入境的 14 岁以下儿童" />
      {hasChildren && (
        <DynamicTable
          rows={rows}
          columns={[
            { key: 'fullName', label: '姓名' },
            { key: 'sex', label: '性别' },
            { key: 'dob', label: '出生日期', type: 'date' },
          ]}
          extraCell={(row, index) => (
            <div className="space-y-1">
              <input type="file" accept="image/*" onChange={(e) => handleFile(index, e.target.files?.[0])} />
              {row.photo && <p className="text-xs text-gray-500">已上传：{row.photo.filename}</p>}
            </div>
          )}
          onChange={setRows}
        />
      )}
    </SectionCard>
  );
}

function ExpensesSection({ data, onChange }) {
  return (
    <SectionCard title="费用与保险">
      <FormRow label="预计费用（美元）" help="填一个大概的就可以，比如1000美元">
        <TextInput value={data.intendedExpenses} onChange={(e) => onChange('intendedExpenses', e.target.value)} />
      </FormRow>
      <FormRow label="是否已购买保险？" help="建议选择 是">
        <SelectInput value={data.boughtInsurance} onChange={(e) => onChange('boughtInsurance', e.target.value)} options={YES_NO_OPTIONS} />
      </FormRow>
      <FormRow label="由谁承担此次费用">
        <SelectInput value={data.expenseSponsor} onChange={(e) => onChange('expenseSponsor', e.target.value)} options={EXPENSE_SPONSORS} />
      </FormRow>
    </SectionCard>
  );
}

function DeclarationsSection({ data, onChange }) {
  return (
    <SectionCard title="声明与授权">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <span className="text-blue-600 text-xl">ℹ️</span>
          <div className="flex-1">
            <p className="text-blue-800 font-semibold mb-2">提交后请注意</p>
            <ul className="text-blue-700 text-sm space-y-2">
              <li className="flex items-start">
                <span className="mr-2">1.</span>
                <span><strong>不要刷新或回退页面</strong>，请耐心等待自动化填表完成</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">2.</span>
                <span>系统会自动填写越南EVISA申请表单，<strong>请等待验证码图片出现</strong></span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">3.</span>
                <span>在"我的 e-Visa 任务"中<strong>查看验证码并填写</strong></span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">4.</span>
                <span>提交验证码后，<strong>等待任务状态显示"完成"</strong>才算申请成功</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <Checkbox
        checked={data.confirmLaws}
        onChange={(val) => onChange('confirmLaws', val)}
        disabled={true}
        label="确认遵守越南法律并按规定申报临时居住"
      />
      <Checkbox
        checked={data.confirmInstructions}
        onChange={(val) => onChange('confirmInstructions', val)}
        disabled={true}
        label="确认已仔细阅读申报说明并准备好全部材料"
      />
      <Checkbox
        checked={data.finalDeclaration}
        onChange={(val) => onChange('finalDeclaration', val)}
        disabled={true}
        label="我保证上述信息真实准确，知悉积分不退"
      />
    </SectionCard>
  );
}

function ToggleTable({ label, checked, onToggle, rows, onRowsChange, columns }) {
  return (
    <div className="border border-emerald-100 rounded-2xl p-5 bg-white space-y-4">
      <Checkbox checked={checked} onChange={onToggle} label={label} />
      {checked && (
        <DynamicTable rows={rows} columns={columns} onChange={onRowsChange} />
      )}
    </div>
  );
}

function DynamicTable({ rows, columns, onChange, extraCell }) {
  const addRow = () => {
    const newRow = {};
    columns.forEach((col) => {
      newRow[col.key] = '';
    });
    onChange([...(rows || []), newRow]);
  };

  const removeRow = (index) => {
    const updated = [...rows];
    updated.splice(index, 1);
    onChange(updated);
  };

  const updateCell = (index, key, value) => {
    const updated = rows.map((row, idx) => (idx === index ? { ...row, [key]: value } : row));
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      {(rows || []).map((row, idx) => (
        <div key={idx} className="grid md:grid-cols-2 gap-4 border border-emerald-100 rounded-2xl p-4 bg-emerald-50/50">
          {columns.map((col) => (
            <FormRow key={col.key} label={col.label}>
              <TextInput
                type={col.type === 'date' ? 'date' : 'text'}
                value={row[col.key] || ''}
                onChange={(e) => updateCell(idx, col.key, e.target.value)}
              />
            </FormRow>
          ))}
          {extraCell && <div>{extraCell(row, idx)}</div>}
          <button
            type="button"
            onClick={() => removeRow(idx)}
            className="text-sm text-rose-600 underline justify-self-end"
          >
            删除
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="px-4 py-2 rounded-full border border-dashed border-emerald-300 text-emerald-700 text-sm"
      >
        + 添加一行
      </button>
    </div>
  );
}
